/**
 * Parse and build the hash for the device timeline page.
 *
 * Hash format:  #device/<hostname>/timeline?from=ISO&to=ISO[&capture=<id>][&burst=<id>]
 *
 * `parseTimelineHash` is intentionally forgiving — unknown params are ignored
 * so future additions don't break the parser.  `buildTimelineHash` only writes
 * params that have values.
 */

export interface TimelineHashState {
  hostname: string | null
  from: string | null
  to: string | null
  runId: string | null
  captureId: string | null
}

export function parseTimelineHash(): TimelineHashState {
  const hash = window.location.hash
  // Expect #device/<hostname>/timeline[?...]
  const pathMatch = hash.match(/^#device\/([^/?]+)\/timeline/)
  const hostname = pathMatch ? decodeURIComponent(pathMatch[1]) : null

  const qi = hash.indexOf('?')
  const params = qi >= 0 ? new URLSearchParams(hash.slice(qi + 1)) : null

  return {
    hostname,
    from: params?.get('from') ?? null,
    to: params?.get('to') ?? null,
    runId: params?.get('run_id') ?? null,
    captureId: params?.get('capture') ?? null,
  }
}

export function buildTimelineHash(opts: {
  hostname: string
  from?: number | null
  to?: number | null
  runId?: string | null
  captureId?: string | null
  /** Open the burst view around this capture instead of the lanes. */
  burstCaptureId?: string | null
}): string {
  const params = new URLSearchParams()
  if (opts.from != null) params.set('from', new Date(opts.from).toISOString())
  if (opts.to != null) params.set('to', new Date(opts.to).toISOString())
  if (opts.runId) params.set('run_id', opts.runId)
  if (opts.captureId) params.set('capture', opts.captureId)
  if (opts.burstCaptureId) params.set('burst', opts.burstCaptureId)
  const qs = params.toString()
  return `#device/${encodeURIComponent(opts.hostname)}/timeline${qs ? `?${qs}` : ''}`
}
